import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth0 } from '@auth0/auth0-react';
import { API_BASE } from '../../shared/Constants';

const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 0.2s ease;
`;

const Modal = styled.div`
  background: ${({ theme }) => theme.modal_background || theme.sidebar_background_solid || theme.sidebar_background};
  border: 1px solid ${({ theme }) => theme.modal_border || theme.sidebar_border};
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 480px;
  padding: 24px;
  color: ${({ theme }) => theme.font_color};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  font-family: 'Noka', sans-serif;
  margin: 0;
`;

const CloseBtn = styled.button`
  all: unset;
  display: flex;
  cursor: pointer;
  color: ${({ theme }) => theme.text_secondary};
  &:hover { color: ${({ theme }) => theme.font_color}; }
`;

const Label = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  margin: 16px 0 6px;
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 11px 13px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.sidebar_border};
  background: transparent;
  color: ${({ theme }) => theme.font_color};
  font-size: 0.95rem;
  outline: none;
  &:focus { border-color: ${({ theme }) => theme.font_color}; }
  &::placeholder { color: ${({ theme }) => theme.text_secondary}; }
`;

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 90px;
  resize: vertical;
  padding: 11px 13px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.sidebar_border};
  background: transparent;
  color: ${({ theme }) => theme.font_color};
  font-family: inherit;
  font-size: 0.95rem;
  line-height: 1.45;
  outline: none;
  &:focus { border-color: ${({ theme }) => theme.font_color}; }
  &::placeholder { color: ${({ theme }) => theme.text_secondary}; }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
`;

const CancelBtn = styled.button`
  all: unset;
  padding: 9px 16px;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.font_color};
  &:hover { background: ${({ theme }) => theme.hover_overlay_light}; }
`;

const CreateBtn = styled.button`
  all: unset;
  padding: 9px 18px;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  background: ${({ theme }) => theme.font_color};
  color: ${({ theme }) => theme.sidebar_background_solid || theme.sidebar_background};
  &:hover { opacity: 0.88; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

interface CreateProjectModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (project: any) => void;
}

const MAX_DESCRIPTION = 500;

const CreateProjectModal: React.FC<CreateProjectModalProps> = ({ open, onClose, onCreated }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setName(''); setDescription(''); }
  }, [open]);

  const handleCreate = async () => {
    const trimmedName = name.trim();
    if (!trimmedName || saving) return;
    setSaving(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${API_BASE}/api/projects/create`, {
        method: 'POST',
        headers: { Authorization: `Auth ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmedName }),
      });
      if (!res.ok) { setSaving(false); return; }
      const data = await res.json();
      const project = data.project;
      const trimmedDesc = description.trim();
      if (project && trimmedDesc) {
        await fetch(`${API_BASE}/api/projects/update`, {
          method: 'POST',
          headers: { Authorization: `Auth ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ project_id: project.project_id, description: trimmedDesc }),
        });
        project.description = trimmedDesc;
      }
      window.dispatchEvent(new CustomEvent('projectsChanged'));
      onCreated(project);
      onClose();
    } catch (e) {
      console.error('Error creating project:', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay $open={open} onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Criar um projeto">
        <Header>
          <Title>Criar um projeto</Title>
          <CloseBtn onClick={onClose} aria-label="Fechar"><CloseIcon /></CloseBtn>
        </Header>

        <Label htmlFor="new-project-name">No que você está trabalhando?</Label>
        <Input
          id="new-project-name"
          data-testid="new-project-name"
          value={name}
          maxLength={120}
          placeholder="Dê um nome ao projeto"
          autoFocus
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
        />

        <Label htmlFor="new-project-desc">O que você quer realizar?</Label>
        <TextArea
          id="new-project-desc"
          data-testid="new-project-desc"
          value={description}
          maxLength={MAX_DESCRIPTION}
          placeholder="Descreva seu projeto, objetivos, assunto, etc..."
          onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESCRIPTION))}
        />

        <Footer>
          <CancelBtn onClick={onClose}>Cancelar</CancelBtn>
          <CreateBtn data-testid="new-project-create" onClick={handleCreate} disabled={!name.trim() || saving}>
            {saving ? 'Criando...' : 'Criar projeto'}
          </CreateBtn>
        </Footer>
      </Modal>
    </Overlay>
  );
};

export default CreateProjectModal;
